import React, { useState, useEffect } from "react";
import config from "../../Config";
import axios from "axios";
import moment from "moment";

/*
 * Compact identity + money strip for the Student Ledger screen.
 *
 * The shared ShowStudent card is built for student administration - it leads with
 * photo, date of birth and Bay Form number. On the ledger screen the person reading
 * is an accountant, so identity is condensed to one line and the space is given to
 * the fee position instead.
 */

const STATUS_LABELS = {
  1: "Normal",
  2: "Struck Off",
  3: "Left",
  4: "Study Complete",
  5: "SLC",
  6: "Freezed",
};

function StudentLedgerHeader({ studentId, outstanding, totalReceived, latestChallan }) {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (!studentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setImgFailed(false);
    axios({
      method: "post",
      url: config.base_url + "Student/GetStudentByID",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType: MemberType || "Admin",
        ID: parseInt(studentId, 10) || 0,
      },
    })
      .then((res) => {
        if (res.data && res.data.status_code === 1) {
          setStudent(res.data.Student || null);
        }
      })
      .catch(() => setStudent(null))
      .finally(() => setLoading(false));
  }, [studentId, AccessKey, UserID, MemberType]);

  if (loading) {
    return (
      <div className="card mb-3">
        <div className="card-body py-3 text-muted small">Loading student…</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="card mb-3">
        <div className="card-body py-3 text-muted small">Student not found.</div>
      </div>
    );
  }

  // Gender is stored as a bit: true = Male. The shared card compares it to the
  // string "male", which never matches, so every student reads as Male there.
  const gender = student.Gender === true || student.Gender === 1 ? "Male" : "Female";
  const status = STATUS_LABELS[student.Status_Type_Id] || "Not Enrolled";
  const isActive = student.Status_Type_Id === 1;
  const photo = config.StudentImagePath + student.Registration_No + ".jpg";

  const initials = (student.Student_Name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const Fact = ({ label, value }) => (
    <div className="me-4 mb-1">
      <div className="text-muted" style={{ fontSize: "11px", lineHeight: 1.1 }}>
        {label}
      </div>
      <div style={{ fontSize: "13px", fontWeight: 600 }}>{value || "—"}</div>
    </div>
  );

  const Money = ({ label, value, tone, note }) => (
    <div className="col">
      <div className="text-muted" style={{ fontSize: "11px" }}>
        {label}
      </div>
      <div className={`fw-bold ${tone || ""}`} style={{ fontSize: "18px", lineHeight: 1.2 }}>
        {value}
      </div>
      {note ? (
        <div className="text-muted" style={{ fontSize: "11px" }}>
          {note}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="card mb-3">
      <div className="card-body py-3">
        <div className="d-flex flex-wrap align-items-start gap-3">
          {/* photo, with initials when the file is missing */}
          <div
            className="flex-shrink-0 d-flex align-items-center justify-content-center bg-light rounded"
            style={{ width: 64, height: 64, overflow: "hidden", fontWeight: 700, color: "#6c757d" }}
          >
            {imgFailed ? (
              <span>{initials}</span>
            ) : (
              <img
                src={photo}
                alt=""
                onError={() => setImgFailed(true)}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
          </div>

          {/* identity */}
          <div className="flex-grow-1">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span style={{ fontSize: "18px", fontWeight: 700 }}>
                {student.Student_Name}
              </span>
              <span className="text-muted">/ {student.Father_Name}</span>
              <span className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}>
                {status}
              </span>
            </div>
            <div className="d-flex flex-wrap mt-2">
              <Fact label="Registration No" value={student.Registration_No} />
              <Fact label="Student ID" value={student.Student_ID} />
              <Fact
                label="Class / Section"
                value={`${student.SelectedClass?.Class_Name || ""}${
                  student.SelectedSection?.Section_Name
                    ? " · " + student.SelectedSection.Section_Name
                    : ""
                }`}
              />
              <Fact label="Wing" value={student.SelectedClass?.SelectedWing?.Wing_Name} />
              <Fact label="Mobile" value={student.Contact_Mobile} />
              <Fact label="Gender" value={gender} />
              <Fact
                label="Joined"
                value={
                  student.Date_Of_Joining
                    ? moment(student.Date_Of_Joining).format("DD/MM/YYYY")
                    : ""
                }
              />
            </div>
          </div>
        </div>

        {/* money position */}
        <div className="row text-center g-2 mt-2 pt-2 border-top">
          <Money
            label="NET OUTSTANDING"
            value={outstanding}
            tone="text-danger"
            note="what the student owes today"
          />
          <Money
            label="TOTAL RECEIVED"
            value={totalReceived}
            tone="text-success"
            note="all payments to date"
          />
          <Money
            label="LATEST VOUCHER"
            value={latestChallan ? latestChallan.amount : "—"}
            note={
              latestChallan
                ? `${latestChallan.label} · ${latestChallan.status}`
                : "no voucher on file"
            }
          />
        </div>
      </div>
    </div>
  );
}

export default StudentLedgerHeader;
